import moment from 'moment';
import { getDateTimeFormatInfo } from './DateTimeUtility';
import { loadFileAsDataUrl, uploadFileFromBase64, uploadFileFromBrowser, FileManagementFileType } from './FileUtility';
import { getRgbaColorString } from './helpers';
import { getCurrentPosition } from './LocationUtility';
import { canIUse, AppFeatures } from './PermissionUtility';

const getExifJsLibrary = () => {
  return import(/* webpackChunkName: "piexifjs" */ 'piexifjs');
};

const getImageExifData = async (imageData) => {
  try {
    const piexifjs = await getExifJsLibrary();
    const result = piexifjs.load(imageData);
    if (result) {
      // 0th refers to the image values. 1st refers to the thumbnail values
      return {
        DateTimeOriginal: result.Exif ? result.Exif[piexifjs.ExifIFD.DateTimeOriginal] : null,
        Orientation: result['0th'] ? result['0th'][piexifjs.ImageIFD.Orientation] : null,
        raw: result
      };
    }
  } catch (err) {
    console.error(err);
  }
  return null;
};

const setImageExifData = async (rawExif, imageData) => {
  const piexifjs = await getExifJsLibrary();
  try {
    // If corrupted exif GPS value (windows crop issue) remove it
    // GD-13792
    if (
      rawExif &&
      rawExif.GPS &&
      rawExif.GPS[piexifjs.GPSIFD.GPSAltitudeRef] &&
      rawExif.GPS[piexifjs.GPSIFD.GPSAltitudeRef].length > 0 &&
      rawExif.GPS[piexifjs.GPSIFD.GPSAltitudeRef][0] > 255
    ) {
      // eslint-disable-next-line no-param-reassign
      delete rawExif.GPS[piexifjs.GPSIFD.GPSAltitudeRef];
    }
    const exifString = piexifjs.dump(rawExif);
    return piexifjs.insert(exifString, imageData);
  } catch (err) {
    console.error(err);
    // If there was an error return original image w/o Exif
    return imageData;
  }
};

export const setImageExifInfo = async (imageData) => {
  const piexif = await getExifJsLibrary();
  const zerothIfd = {};
  const exifIfd = {};
  const gpsIfd = {};

  zerothIfd[piexif.ImageIFD.Software] = 'GoFormz';
  exifIfd[piexif.ExifIFD.DateTimeOriginal] = moment().format('YYYY:MM:DD HH:mm:ss');

  const gpsLocation = await getCurrentPosition({
    timeout: 6000, // 6 seconds
    maximumAge: 60 * 60000 // 60 minutes
  });
  if (gpsLocation && gpsLocation.coords) {
    gpsIfd[piexif.GPSIFD.GPSLatitudeRef] = gpsLocation.coords.latitude < 0 ? 'S' : 'N';
    gpsIfd[piexif.GPSIFD.GPSLatitude] = piexif.GPSHelper.degToDmsRational(Math.abs(gpsLocation.coords.latitude));
    gpsIfd[piexif.GPSIFD.GPSLongitudeRef] = gpsLocation.coords.longitude < 0 ? 'W' : 'E';
    gpsIfd[piexif.GPSIFD.GPSLongitude] = piexif.GPSHelper.degToDmsRational(Math.abs(gpsLocation.coords.longitude));
    gpsIfd[piexif.GPSIFD.GPSDateStamp] = moment().format('YYYY:MM:DD HH:mm:ss');
  }

  const exif = { '0th': zerothIfd, Exif: exifIfd, GPS: gpsIfd };
  return setImageExifData(exif, imageData);
};

const resetOrientation = async (rawExif) => {
  const piexifjs = await getExifJsLibrary();
  if (rawExif && rawExif['0th']) {
    // eslint-disable-next-line no-param-reassign
    rawExif['0th'][piexifjs.ImageIFD.Orientation] = 1;
  }
};

export const getImageDate = (imageData) => {
  if (imageData && imageData.DateTimeOriginal) {
    const date = moment(imageData.DateTimeOriginal, 'YYYY:MM:DD HH:mm:ss');
    if (date.isValid()) {
      const dateFormat = getDateTimeFormatInfo().fullDateTimePattern;
      return date.format(dateFormat);
    }
  }
  return 'No Date';
};

export const loadImage = (file: string | File | Blob, allowCors = false) =>
  new Promise<HTMLImageElement>(async (callback) => {
    const image = new Image();
    if (allowCors) {
      image.crossOrigin = 'Anonymous';
    }
    image.onload = () => {
      callback(image);
    };
    let imageData;
    if (typeof file === 'string') {
      imageData = file;
    } else {
      imageData = await loadFileAsDataUrl(file);
    }
    image.src = imageData;
  });

const getTargetDimensions = (imageWidth, imageHeight, fieldWidth, fieldHeight, imageQuality, orientation) => {
  const targetSize = 1280;
  const maxWidth = imageQuality === 'low' ? Math.min(fieldWidth, targetSize) : targetSize;
  const maxHeight = imageQuality === 'low' ? Math.min(fieldHeight, targetSize) : targetSize;
  let width = orientation > 4 ? imageHeight : imageWidth;
  let height = orientation > 4 ? imageWidth : imageHeight;
  if (width > height) {
    if (width > maxWidth) {
      height *= maxWidth / width;
      width = maxWidth;
    }
  } else if (height > maxHeight) {
    width *= maxHeight / height;
    height = maxHeight;
  }
  return {
    rotatedWidth: width,
    rotatedHeight: height,
    width: orientation > 4 ? height : width,
    height: orientation > 4 ? width : height
  };
};

const calculateAdjustedFontSize = (fontSize, imageWidth, imageHeight, fieldWidth, fieldHeight) => {
  const isImageLandscape = imageWidth > imageHeight;
  if (isImageLandscape && imageWidth > fieldWidth) {
    return (imageWidth / fieldWidth) * fontSize;
  }

  if (!isImageLandscape && imageHeight > fieldHeight) {
    return (imageHeight / fieldHeight) * fontSize;
  }

  return fontSize;
};

const drawImage = (canvas, image, width, height, orientation) => {
  const ctx = canvas.getContext('2d');
  ctx.save();
  if (orientation) {
    switch (orientation) {
      case 2:
        ctx.translate(width, 0);
        ctx.scale(-1, 1);
        break;
      case 3:
        ctx.translate(width, height);
        ctx.rotate(Math.PI);
        break;
      case 4:
        ctx.translate(0, height);
        ctx.scale(1, -1);
        break;
      case 5:
        ctx.rotate(0.5 * Math.PI);
        ctx.scale(1, -1);
        break;
      case 6:
        ctx.rotate(0.5 * Math.PI);
        ctx.translate(0, -height);
        break;
      case 7:
        ctx.rotate(0.5 * Math.PI);
        ctx.translate(width, -height);
        ctx.scale(-1, 1);
        break;
      case 8:
        ctx.rotate(-0.5 * Math.PI);
        ctx.translate(-width, 0);
        break;
      default:
        // no-op
        break;
    }
  }

  ctx.drawImage(image, 0, 0, width, height);
  ctx.restore();
};

const drawDateStamp = (canvas, text, fontSize, fontName, foregroundColor) => {
  const ctx = canvas.getContext('2d');

  ctx.font = `${fontSize}px ${fontName}`;
  ctx.fillStyle = getRgbaColorString(foregroundColor);

  const textSize = ctx.measureText(text);
  ctx.fillText(text, canvas.width - textSize.width - 20, canvas.height - fontSize);
};

export const cropImage = async (image, x, y, w, h) => {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext('2d');
  ctx.save();

  ctx.drawImage(image, x, y, w, h, 0, 0, w, h);
  ctx.restore();

  return canvas.toDataURL('image/jpeg');
};

function browserImageAutoRotates() {
  return getComputedStyle(document.body).imageOrientation === 'from-image';
}

export const resizeImage = async (file, imageQuality, fieldWidth, fieldHeight, dateStampOptions) => {
  let imageData;
  if (typeof file === 'string') {
    imageData = file;
  } else {
    imageData = await loadFileAsDataUrl(file);
  }

  const image = await loadImage(imageData);
  const exifData = await getImageExifData(imageData);
  const rawExifData = exifData ? exifData.raw : null;

  let orientation = 1;

  // Starting in Chrome 81, images are auto rotated based on the orientation.
  // We only want to apply our manual rotation for browsers that don't auto rotate
  if (!browserImageAutoRotates()) {
    if (exifData && exifData.Orientation !== undefined) {
      orientation = exifData.Orientation;
    }
  }

  const { width, height, rotatedWidth, rotatedHeight } = getTargetDimensions(image.width, image.height, fieldWidth, fieldHeight, imageQuality, orientation);

  const canvas = document.createElement('canvas');
  canvas.width = rotatedWidth;
  canvas.height = rotatedHeight;

  drawImage(canvas, image, width, height, orientation);

  if (dateStampOptions && dateStampOptions.dateStamp) {
    const fontSize = calculateAdjustedFontSize(dateStampOptions.fontSize, rotatedWidth, rotatedHeight, fieldWidth, fieldHeight);
    drawDateStamp(canvas, getImageDate(exifData), fontSize, dateStampOptions.fontName, dateStampOptions.foregroundColor);
  }

  const resizedImage = canvas.toDataURL('image/jpeg');

  if (rawExifData) {
    await resetOrientation(rawExifData);
    return setImageExifData(rawExifData, resizedImage);
  }

  return resizedImage;
};

export const generateThumbnail = async (file) => {
  let imageData;
  if (typeof file === 'string') {
    imageData = file;
  } else {
    imageData = await loadFileAsDataUrl(file);
  }

  const image = await loadImage(imageData, true);
  const baseSize = 250;
  let width = 0;
  let height = 0;

  if (image.width > image.height) {
    width = baseSize;
    height = (image.height / image.width) * width;
  } else {
    height = baseSize;
    width = (image.width / image.height) * height;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  ctx.save();
  ctx.drawImage(image as CanvasImageSource, 0, 0, canvas.width, canvas.height);
  ctx.restore();

  return { dataUri: canvas.toDataURL('image/jpeg'), width: canvas.width, height: canvas.height };
};

export const uploadImage = async (originalFile, resizedImage, retainImageAsAttachment, formId, fieldName, fieldId) => {
  let originalFileId;
  if (canIUse(AppFeatures.RetainImageAsAttachment) && retainImageAsAttachment === 'original') {
    let originalImageValues;
    const fileName = `${fieldName} (Original)`;
    const description = `This is the original file for ${fieldName}`;
    const image = await loadImage(originalFile);
    if (typeof originalFile === 'string') {
      originalImageValues = await uploadFileFromBase64(
        originalFile,
        image.width,
        image.height,
        false,
        false,
        formId,
        FileManagementFileType.Original,
        fileName,
        description,
        null,
        fieldId
      );
    } else {
      originalImageValues = await uploadFileFromBrowser(
        originalFile,
        false,
        fileName,
        description,
        formId,
        image.width,
        image.height,
        null,
        FileManagementFileType.Original,
        true,
        fieldId,
        false
      );
    }
    originalFileId = originalImageValues.fileId;
  }

  const resizedImg = await loadImage(resizedImage);
  const { fileId, attachmentId } = await uploadFileFromBase64(
    resizedImage,
    resizedImg.width,
    resizedImg.height,
    true,
    true,
    formId,
    FileManagementFileType.Embedded,
    fieldName,
    '',
    originalFileId,
    fieldId
  );

  return { fileId, attachmentId };
};

export const rescaleImageInModal = (imgW, imgH, modalWidth) => {
  const w = window;
  const d = document;
  const documentElement = d.documentElement;
  const body = d.getElementsByTagName('body')[0];
  const height = w.innerHeight || documentElement.clientHeight || body.clientHeight;

  let resizedWidth = null;
  let resizedHeight = null;
  const modalAddedHeight = 260;
  const modalAddedWidth = 62;

  const imgHeight = imgH;
  const imgWidth = imgW;
  const modalContentHeight = height - modalAddedHeight < 100 ? 100 : height - modalAddedHeight;
  const modalContentWidth = modalWidth - modalAddedWidth;

  resizedHeight = modalContentHeight;
  resizedWidth = modalContentHeight * (imgWidth / imgHeight);
  if (resizedWidth > modalContentWidth) {
    resizedWidth = modalContentWidth;
    resizedHeight = modalContentWidth * (imgHeight / imgWidth);
  }

  return { resizedWidth, resizedHeight };
};

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = [].slice.call(new Uint8Array(buffer));

  // eslint-disable-next-line no-return-assign
  bytes.forEach((b) => (binary += String.fromCharCode(b)));

  return window.btoa(binary);
}

export const getUriWithTimeStamp = async (url, width, height, fontSize, fontName, foregroundColor) => {
  const response = await fetch(url);
  if (response.status >= 400) {
    throw new Error(`${response.status} : ${response.statusText}`);
  }

  const contentType = response.headers.get('content-type');
  const data = await response.arrayBuffer();
  const imageStr = arrayBufferToBase64(data);
  const dataUri = `data:${contentType};base64,${imageStr}`;
  const image = await loadImage(dataUri);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image as CanvasImageSource, 0, 0, width, height);
  drawDateStamp(canvas, new Date().toLocaleString(), fontSize, fontName, foregroundColor);
  return canvas.toDataURL('image/jpeg');
};

export const uploadUri = async (uri, width, height, formId, fieldId) => {
  return uploadFileFromBase64(uri, width, height, false, false, formId, FileManagementFileType.Original, null, null, null, fieldId);
};
